import type { Page, PageSection, HomeViewData } from '@/types/page';
import type { TenantConfig } from '@/types/tenant';

/**
 * Maps backend Page data to frontend HomeViewData format
 * This class contains only pure functions to avoid causing re-renders
 */
export class PageMapper {
  // Cache for default data to avoid recreation
  private static defaultStatsCache: Record<string, any> | null = null;
  private static defaultServicesCache: Record<string, any> | null = null;

  /**
   * Convert Page with sections to HomeViewData or appropriate format based on page type
   */
  static toViewData(page: Page, tenant?: TenantConfig): any {
    if (!page) {
      return this.getDefaultHomeViewData(tenant);
    }

    // Route to appropriate mapper based on page type
    switch (page.type) {
      case 'HOME':
        return this.toHomeViewData(page, tenant);
      case 'ABOUT':
        return this.toAboutViewData(page, tenant);
      case 'SERVICES':
        return this.toServicesViewData(page, tenant);
      case 'CONTACT':
        return this.toContactViewData(page, tenant);
      default:
        return this.toGenericViewData(page, tenant);
    }
  }

  /**
   * Convert Page with sections to HomeViewData
   */
  static toHomeViewData(page: Page, tenant?: TenantConfig): HomeViewData {
    if (!page) {
      return this.getDefaultHomeViewData(tenant);
    }

    const sections = page.sections || [];

    // Get sections by type
    const heroSection = sections.find(s => s.type === 'HERO' && s.enabled);
    const statsSection = sections.find(s => s.type === 'STATS' && s.enabled);
    const featuredPropsSection = sections.find(s => s.type === 'FEATURED_PROPERTIES' && s.enabled);
    const servicesSection = sections.find(s => s.type === 'SERVICES' && s.enabled);
    const blogSection = sections.find(s => s.type === 'BLOG_PREVIEW' && s.enabled);
    const ctaSection = sections.find(s => s.type === 'CTA' && s.enabled);

    return {
      hero: this.mapHeroSection(heroSection, page, tenant),
      stats: this.mapStatsSection(statsSection),
      featuredProperties: this.mapFeaturedPropertiesSection(featuredPropsSection),
      services: this.mapServicesSection(servicesSection),
      blog: this.mapBlogSection(blogSection, tenant),
      cta: this.mapCtaSection(ctaSection),
      seo: this.mapSeoData(page, tenant)
    };
  }

  /**
   * Convert ABOUT page to view data
   */
  static toAboutViewData(page: Page, tenant?: TenantConfig): any {
    const sections = page.sections || [];

    const heroSection = sections.find(s => s.type === 'HERO' && s.enabled);
    const introSection = sections.find(s => s.type === 'INTRO' && s.enabled);
    const missionSection = sections.find(s => s.type === 'MISSION' && s.enabled);
    const teamSection = sections.find(s => s.type === 'TEAM' && s.enabled);
    const ctaSection = sections.find(s => s.type === 'CTA' && s.enabled);

    return {
      hero: {
        enabled: !!heroSection,
        title: heroSection?.title || page.title || 'Quiénes Somos',
        subtitle: heroSection?.subtitle || heroSection?.content?.subtitle || page.description || '',
        backgroundImage: heroSection?.content?.backgroundImage || '/images/hero/about-hero.jpg',
        showSearch: heroSection?.content?.showSearch ?? false,
        showCTA: heroSection?.content?.showCTA ?? false,
        ctaText: heroSection?.content?.ctaText || '',
        ctaLink: heroSection?.content?.ctaLink || '#'
      },
      intro: introSection ? {
        enabled: true,
        title: introSection.title || 'Nuestra Historia',
        subtitle: introSection.subtitle || introSection.content?.subtitle || '',
        content: introSection.content?.description || '',
        highlights: introSection.content?.highlights || [],
        image: introSection.content?.image || '/images/about/about-intro.jpg',
        imageAlt: introSection.content?.imageAlt || 'Nuestra empresa'
      } : { enabled: false },
      mission: missionSection ? {
        enabled: true,
        title: missionSection.title || 'Misión y Valores',
        items: missionSection.content?.items || [],
        mission: missionSection.content?.mission || '',
        vision: missionSection.content?.vision || '',
        values: missionSection.content?.values || [],
        backgroundImage: missionSection.content?.backgroundImage || '/images/about/mission-bg.jpg'
      } : { enabled: false },
      team: teamSection ? {
        enabled: true,
        title: teamSection.title || 'Nuestro Equipo',
        subtitle: teamSection.subtitle || '',
        description: teamSection.content?.description || '',
        members: teamSection.content?.members || [],
        showAll: teamSection.content?.showAll ?? true
      } : { enabled: false },
      cta: this.mapCtaSection(ctaSection),
      seo: this.mapSeoData(page, tenant)
    };
  }

  /**
   * Convert SERVICES page to view data
   */
  static toServicesViewData(page: Page, tenant?: TenantConfig): any {
    return this.toGenericViewData(page, tenant);
  }

  /**
   * Convert CONTACT page to view data
   */
  static toContactViewData(page: Page, tenant?: TenantConfig): any {
    return this.toGenericViewData(page, tenant);
  }

  /**
   * Convert any generic page to view data
   */
  static toGenericViewData(page: Page, tenant?: TenantConfig): any {
    const sections = page.sections || [];

    // Find all section types
    const heroSection = sections.find(s => s.type === 'HERO' && s.enabled);
    const introSection = sections.find(s => s.type === 'INTRO' && s.enabled);
    const missionSection = sections.find(s => s.type === 'MISSION' && s.enabled);
    const teamSection = sections.find(s => s.type === 'TEAM' && s.enabled);
    const statsSection = sections.find(s => s.type === 'STATS' && s.enabled);
    const timelineSection = sections.find(s => s.type === 'TIMELINE' && s.enabled);
    const cultureSection = sections.find(s => s.type === 'CULTURE' && s.enabled);
    const certificationsSection = sections.find(s => s.type === 'CERTIFICATIONS' && s.enabled);
    const ctaSection = sections.find(s => s.type === 'CTA' && s.enabled);

    return {
      hero: this.mapHeroSection(heroSection, page, tenant),
      intro: introSection ? {
        enabled: true,
        title: introSection.title || 'Nuestra Historia',
        subtitle: introSection.content?.subtitle || '',
        content: introSection.content?.description || '',
        highlights: introSection.content?.highlights || [],
        image: introSection.content?.image || '/images/about/about-intro.jpg',
        imageAlt: introSection.content?.imageAlt || 'Nuestra empresa'
      } : { enabled: false },
      mission: missionSection ? {
        enabled: true,
        title: missionSection.title || 'Misión y Valores',
        items: missionSection.content?.items || [],
        mission: missionSection.content?.mission || '',
        vision: missionSection.content?.vision || '',
        values: missionSection.content?.values || [],
        backgroundImage: missionSection.content?.backgroundImage || '/images/about/mission-bg.jpg'
      } : { enabled: false },
      team: teamSection ? {
        enabled: true,
        title: teamSection.title || 'Nuestro Equipo',
        subtitle: teamSection.subtitle || '',
        description: teamSection.content?.description || '',
        members: teamSection.content?.members || [],
        showAll: teamSection.content?.showAll ?? true
      } : { enabled: false },
      stats: statsSection ? {
        enabled: true,
        title: statsSection.title || 'Nuestros Números',
        subtitle: statsSection.subtitle || '',
        items: statsSection.content?.items || [],
        backgroundImage: statsSection.content?.backgroundImage || '/images/about/stats-bg.jpg'
      } : { enabled: false },
      timeline: timelineSection ? {
        enabled: true,
        title: timelineSection.title || 'Nuestra Trayectoria',
        subtitle: timelineSection.subtitle || '',
        events: timelineSection.content?.events || []
      } : { enabled: false },
      culture: cultureSection ? {
        enabled: true,
        title: cultureSection.title || 'Nuestra Cultura',
        subtitle: cultureSection.subtitle || '',
        values: cultureSection.content?.values || [],
        backgroundImage: cultureSection.content?.backgroundImage || '/images/about/culture-bg.jpg'
      } : { enabled: false },
      certifications: certificationsSection ? {
        enabled: true,
        title: certificationsSection.title || 'Certificaciones',
        subtitle: certificationsSection.subtitle || '',
        items: certificationsSection.content?.items || []
      } : { enabled: false },
      cta: this.mapCtaSection(ctaSection),
      seo: this.mapSeoData(page, tenant),
      sections: sections.filter(s => s.enabled)
    };
  }

  /**
   * Map HERO section
   */
  private static mapHeroSection(section: PageSection | undefined, page: Page, tenant?: TenantConfig): HomeViewData['hero'] {
    const content = section?.content || {};
    const companyName = tenant?.name || 'Inmobiliaria';

    return {
      title: section?.title || content.title || `Bienvenido a ${companyName}`,
      subtitle: section?.subtitle || content.subtitle || `Encuentra tu propiedad ideal con ${companyName}`,
      backgroundImage: content.backgroundImage || '/images/hero/default-hero.jpg',
      showSearch: content.showSearch ?? true,
      showCTA: content.showCTA ?? false,
      ctaText: content.ctaText || 'Comenzar',
      ctaLink: content.ctaLink || '#'
    };
  }

  /**
   * Map STATS section
   */
  private static mapStatsSection(section: PageSection | undefined): HomeViewData['stats'] {
    const content = section?.content || {};
    
    // Use cached default stats to avoid recreation
    if (!this.defaultStatsCache) {
      this.defaultStatsCache = [
        { icon: 'TrendingUp', value: '1000+', label: 'Propiedades' },
        { icon: 'Users', value: '500+', label: 'Clientes Satisfechos' },
        { icon: 'Award', value: '15', label: 'Años de Experiencia' },
        { icon: 'CheckCircle', value: '95%', label: 'Tasa de Éxito' }
      ];
    }

    return {
      items: content.items || this.defaultStatsCache,
      backgroundImage: content.backgroundImage || '/images/hero/default-hero.jpg'
    };
  }

  /**
   * Map FEATURED_PROPERTIES section
   */
  private static mapFeaturedPropertiesSection(section: PageSection | undefined): HomeViewData['featuredProperties'] {
    const content = section?.content || {};
    
    return {
      title: content.title || 'Propiedades Destacadas',
      description: content.description || 'Descubre las mejores propiedades seleccionadas especialmente para ti',
      limit: content.limit || 6,
      showViewAll: content.showViewAll ?? true
    };
  }

  /**
   * Map SERVICES section
   */
  private static mapServicesSection(section: PageSection | undefined): HomeViewData['services'] {
    const content = section?.content || {};
    
    // Use cached default services to avoid recreation
    if (!this.defaultServicesCache) {
      this.defaultServicesCache = [
        {
          icon: 'Home',
          title: 'Compra y Venta',
          description: 'Te ayudamos a encontrar tu hogar ideal o vender tu propiedad'
        },
        {
          icon: 'Key',
          title: 'Arrendamiento',
          description: 'Gestión completa de arriendos para propietarios e inquilinos'
        },
        {
          icon: 'Calculator',
          title: 'Tasaciones',
          description: 'Evaluaciones profesionales del valor de tu propiedad'
        },
        {
          icon: 'Scale',
          title: 'Asesoría Legal',
          description: 'Acompañamiento legal en todos tus procesos inmobiliarios'
        }
      ];
    }

    return {
      title: content.title || 'Nuestros Servicios',
      description: content.description || 'Ofrecemos una gama completa de servicios inmobiliarios',
      backgroundImage: content.backgroundImage || '/images/hero/default-hero.jpg',
      items: content.items || this.defaultServicesCache
    };
  }

  /**
   * Map BLOG_PREVIEW section
   */
  private static mapBlogSection(section: PageSection | undefined, tenant?: TenantConfig): HomeViewData['blog'] {
    const content = section?.content || {};
    const blogEnabled = tenant?.features?.blog ?? true;
    
    return {
      enabled: blogEnabled && (content.enabled ?? true),
      title: content.title || 'Últimas Noticias',
      description: content.description || 'Mantente informado con las últimas tendencias del mercado inmobiliario',
      limit: content.limit || 3,
      showViewMore: content.showViewMore ?? true,
      featuredOnly: content.featuredOnly ?? true
    };
  }

  /**
   * Map CTA section
   */
  private static mapCtaSection(section: PageSection | undefined): HomeViewData['cta'] {
    if (!section) return undefined;

    const content = section.content || {};

    return {
      title: section.title || content.title || '¿Listo para encontrar tu próximo hogar?',
      description: section.subtitle || content.description || 'Contáctanos hoy y te ayudaremos a hacer realidad tus sueños inmobiliarios',
      backgroundImage: content.backgroundImage || '/images/hero/default-hero.jpg',
      primaryButton: {
        text: content.primaryButton?.text || 'Ver Propiedades',
        link: content.primaryButton?.link || '/ventas'
      },
      secondaryButton: {
        text: content.secondaryButton?.text || 'Contáctanos',
        link: content.secondaryButton?.link || '/contacto'
      }
    };
  }

  /**
   * Map SEO data from page
   */
  private static mapSeoData(page: Page, tenant?: TenantConfig): HomeViewData['seo'] {
    const companyName = tenant?.name || 'Inmobiliaria';
    
    return {
      title: page.seoTitle || page.title || `${companyName} - Tu hogar ideal te espera`,
      description: page.seoDescription || page.description || `Encuentra tu propiedad ideal con ${companyName}`,
      keywords: (page.seoKeywords || []).join(', ') || 'inmobiliaria, propiedades, casas, departamentos',
      imageAlt: `${companyName} - Propiedades de calidad`
    };
  }

  /**
   * Create default HomeViewData when no page is available
   * This function is memoized to avoid recreation
   */
  static getDefaultHomeViewData(tenant?: TenantConfig): HomeViewData {
    const companyName = tenant?.name || 'Inmobiliaria';
    
    // Use cached defaults to avoid recreation
    if (!this.defaultStatsCache) {
      this.defaultStatsCache = [
        { icon: 'TrendingUp', value: '1000+', label: 'Propiedades' },
        { icon: 'Users', value: '500+', label: 'Clientes Satisfechos' },
        { icon: 'Award', value: '15', label: 'Años de Experiencia' },
        { icon: 'CheckCircle', value: '95%', label: 'Tasa de Éxito' }
      ];
    }

    if (!this.defaultServicesCache) {
      this.defaultServicesCache = [
        {
          icon: 'Home',
          title: 'Compra y Venta',
          description: 'Te ayudamos a encontrar tu hogar ideal o vender tu propiedad'
        },
        {
          icon: 'Key',
          title: 'Arrendamiento',
          description: 'Gestión completa de arriendos para propietarios e inquilinos'
        },
        {
          icon: 'Calculator',
          title: 'Tasaciones',
          description: 'Evaluaciones profesionales del valor de tu propiedad'
        },
        {
          icon: 'Scale',
          title: 'Asesoría Legal',
          description: 'Acompañamiento legal en todos tus procesos inmobiliarios'
        }
      ];
    }
    
    return {
      hero: {
        title: `Bienvenido a ${companyName}`,
        subtitle: `Encuentra tu propiedad ideal con ${companyName}`,
        backgroundImage: '/images/hero/default-hero.jpg',
        showSearch: true
      },
      stats: {
        items: this.defaultStatsCache,
        backgroundImage: '/images/hero/default-hero.jpg'
      },
      featuredProperties: {
        title: 'Propiedades Destacadas',
        description: 'Descubre las mejores propiedades seleccionadas especialmente para ti',
        limit: 6,
        showViewAll: true
      },
      services: {
        title: 'Nuestros Servicios',
        description: 'Ofrecemos una gama completa de servicios inmobiliarios',
        backgroundImage: '/images/hero/default-hero.jpg',
        items: this.defaultServicesCache
      },
      blog: {
        enabled: tenant?.features?.blog ?? true,
        title: 'Últimas Noticias',
        description: 'Mantente informado con las últimas tendencias del mercado inmobiliario',
        limit: 3,
        showViewMore: true,
        featuredOnly: true
      },
      cta: {
        title: '¿Listo para encontrar tu próximo hogar?',
        description: 'Contáctanos hoy y te ayudaremos a hacer realidad tus sueños inmobiliarios',
        backgroundImage: '/images/hero/default-hero.jpg',
        primaryButton: {
          text: 'Ver Propiedades',
          link: '/ventas'
        },
        secondaryButton: {
          text: 'Contáctanos',
          link: '/contacto'
        }
      },
      seo: {
        title: `${companyName} - Tu hogar ideal te espera`,
        description: `Encuentra tu propiedad ideal con ${companyName}. Casas, departamentos y más propiedades de calidad.`,
        keywords: 'inmobiliaria, propiedades, casas, departamentos, arriendo, venta',
        imageAlt: `${companyName} - Propiedades de calidad`
      }
    };
  }

  /**
   * Validate and clean page data
   */
  static validatePageData(page: Page): boolean {
    return !!(
      page &&
      page.id &&
      page.title &&
      page.slug &&
      page.type
    );
  }

  /**
   * Sort sections by order
   */
  static sortSectionsByOrder(sections: PageSection[]): PageSection[] {
    return [...sections].sort((a, b) => a.order - b.order);
  }

  /**
   * Filter enabled sections
   */
  static filterEnabledSections(sections: PageSection[]): PageSection[] {
    return sections.filter(section => section.enabled);
  }

  /**
   * Get section by type
   */
  static getSectionByType(sections: PageSection[], type: string): PageSection | undefined {
    return sections.find(section => section.type === type && section.enabled);
  }

  /**
   * Clear cache (useful for testing or when needed)
   */
  static clearCache(): void {
    this.defaultStatsCache = null;
    this.defaultServicesCache = null;
  }
}

export default PageMapper;